/**
 * Utility functions for transforming CSV data for bulk onboarding
 */

/**
 * Safely trim a value if it's a string
 */
const safeTrim = (value) => {
  return typeof value === 'string' ? value.trim() : String(value).trim();
};

/**
 * Convert Excel serial date to ISO date string
 */
export const excelSerialToDate = (serial) => {
  const excelEpoch = new Date(Date.UTC(1899, 11, 30));
  const date = new Date(excelEpoch.getTime() + serial * 86400000);
  return date.toISOString().split("T")[0];
};

/**
 * Normalize row data: convert Excel dates, trim strings, etc.
 */
export const normalizeUserRow = (row, rowIndex) => {
  const entityType = row.entity_type ? safeTrim(row.entity_type).toLowerCase() : "";
  let resolvedRole = "Employee";
  if (entityType === "admin" || (row.role && safeTrim(row.role).toLowerCase() === "admin")) {
    resolvedRole = "Admin";
  } else if (row.role) {
    resolvedRole = safeTrim(row.role);
  }

  const normalized = {
    row_number: rowIndex + 1,
    external_row_id: row.external_row_id || `ROW-${rowIndex + 1}`,
    email: row.email ? safeTrim(row.email) : undefined,
    password: row.password ? safeTrim(row.password) : undefined,
    first_name: row.first_name ? safeTrim(row.first_name) : undefined,
    last_name: row.last_name ? safeTrim(row.last_name) : undefined,
    role: resolvedRole,
  };

  // Handle demographics
  const demographics = {};
  
  if (row.gender) {
    demographics.gender = safeTrim(row.gender).toLowerCase();
  }
  
  if (row.age) {
    demographics.age = safeTrim(row.age);
  }
  
  if (row.pronouns) {
    demographics.pronouns = safeTrim(row.pronouns);
  }
  
  if (row.language) {
    demographics.language = safeTrim(row.language);
  }
  
  if (row.location) {
    demographics.location = safeTrim(row.location);
  }
  
  if (row.position) {
    demographics.position = safeTrim(row.position);
  }
  
  if (row.phone_number || row.employee_phone) {
    demographics.phone_number = safeTrim(row.phone_number || row.employee_phone);
  }

  if (Object.keys(demographics).length > 0) {
    normalized.demographics = demographics;
  }

  return normalized;
};

/**
 * Normalize superuser data
 */
export const normalizeSuperuserRow = (row) => {
  const normalized = {
    email: row.email ? safeTrim(row.email) : undefined,
    first_name: row.first_name ? safeTrim(row.first_name) : undefined,
    last_name: row.last_name ? safeTrim(row.last_name) : undefined,
    password: row.password ? safeTrim(row.password) : undefined,
    role: "SuperUser",
  };

  // Handle demographics
  const demographics = {};
  
  if (row.gender) {
    demographics.gender = safeTrim(row.gender).toLowerCase();
  }
  
  if (row.age) {
    demographics.age = safeTrim(row.age);
  }
  
  if (row.pronouns) {
    demographics.pronouns = safeTrim(row.pronouns);
  }
  
  if (row.language) {
    demographics.language = safeTrim(row.language);
  }
  
  if (row.location) {
    demographics.location = safeTrim(row.location);
  }
  
  if (row.position) {
    demographics.position = safeTrim(row.position);
  }
  
  if (row.phone_number) {
    demographics.phone_number = safeTrim(row.phone_number);
  }

  if (Object.keys(demographics).length > 0) {
    normalized.demographics = demographics;
  }

  return normalized;
};

/**
 * Normalize company data
 */
export const normalizeCompanyRow = (row) => {
  const code =
    row.company_code ??
    row.companycode ??
    row.companyCode ??
    row["Company Code"] ??
    row["Company code"];
  const name =
    row.company_name ??
    row.companyname ??
    row.companyName ??
    row["Company Name"];
  const email =
    row.contact_email ??
    row.contactemail ??
    row.contactEmail ??
    row["Contact Email"];
  const phone =
    row.phone_number ??
    row.phonenumber ??
    row.phoneNumber ??
    row["Phone Number"];

  return {
    company_name: name ? safeTrim(name) : undefined,
    contact_email: email ? safeTrim(email) : undefined,
    phone_number: phone ? safeTrim(phone) : undefined,
    company_code: code ? safeTrim(code) : undefined,
  };
};


/**
 * Build bulk onboard payload from CSV data
 * Supports multiple formats:
 * 1. Multiple sheets: Company, SuperUser, Users sheets
 * 2. Single sheet with entity_type column: company|superuser|user|admin|employee
 */
export const buildBulkOnboardPayload = (workbook, timestamp) => {
  const XLSX = require("xlsx");
  const sheets = workbook.SheetNames;
  
  let companySheet = null;
  let superuserSheet = null;
  let usersSheet = null;

  // Find sheet by name (case-insensitive)
  for (const sheetName of sheets) {
    const upper = sheetName.toUpperCase();
    if (upper.includes("COMPANY")) {
      companySheet = sheetName;
    } else if (upper.includes("SUPERUSER") || upper.includes("SUPER USER")) {
      superuserSheet = sheetName;
    } else if (upper.includes("USER") || upper.includes("EMPLOYEE") || upper.includes("STAFF")) {
      usersSheet = sheetName;
    }
  }

  // If single sheet with all data - check for entity_type column
  if (!companySheet && !superuserSheet && !usersSheet && sheets.length === 1) {
    const sheetName = sheets[0];
    const worksheet = workbook.Sheets[sheetName];
    const data = XLSX.utils.sheet_to_json(worksheet);
    
    // Check if data has entity_type column
    if (data && data.length > 0 && data[0].entity_type) {
      // Parse by entity_type
      const companyRows = data.filter((r) => safeTrim(r.entity_type || '').toLowerCase() === 'company');
      const superuserRows = data.filter((r) => safeTrim(r.entity_type || '').toLowerCase() === 'superuser');
      const userRows = data.filter((r) => {
        const kind = safeTrim(r.entity_type || '').toLowerCase();
        return kind === 'user' || kind === 'admin' || kind === 'employee';
      });
      
      if (!companyRows.length) {
        throw new Error("No company row found. Add a row with entity_type='company'");
      }
      if (!superuserRows.length) {
        throw new Error("No superuser row found. Add a row with entity_type='superuser'");
      }
      
      const companyRow = normalizeCompanyRow(companyRows[0]);
      const superuserRow = normalizeSuperuserRow(superuserRows[0]);
      const parsedUserRows = userRows.map((row, idx) => normalizeUserRow(row, idx));
      
      return {
        request_id: `bulk-onboard-${timestamp}`,
        company: companyRow,
        superuser: superuserRow,
        users: parsedUserRows,
        options: {
          allow_partial_user_success: true,
        },
      };
    } else {
      // Fallback: treat single sheet as containing all data types
      companySheet = sheetName;
      superuserSheet = sheetName;
      usersSheet = sheetName;
    }
  }

  if (!companySheet) {
    throw new Error(
      "CSV must have a sheet named 'Company' with company_name, contact_email, phone_number"
    );
  }

  // Parse company data
  const companyWorksheet = workbook.Sheets[companySheet];
  const companyData = XLSX.utils.sheet_to_json(companyWorksheet);

  if (!companyData || companyData.length === 0) {
    throw new Error("Company sheet is empty");
  }

  const companyRow = normalizeCompanyRow(companyData[0]);

  // Parse superuser data
  let superuserRow = null;
  if (superuserSheet) {
    const superuserWorksheet = workbook.Sheets[superuserSheet];
    const superuserData = XLSX.utils.sheet_to_json(superuserWorksheet);
    if (superuserData && superuserData.length > 0) {
      superuserRow = normalizeSuperuserRow(superuserData[0]);
    }
  }

  if (!superuserRow) {
    throw new Error(
      "Superuser data not found. Add superuser email, first_name, last_name, password to CSV"
    );
  }

  // Parse users data
  let userRows = [];
  if (usersSheet) {
    const usersWorksheet = workbook.Sheets[usersSheet];
    const usersData = XLSX.utils.sheet_to_json(usersWorksheet);
    if (usersData && usersData.length > 0) {
      userRows = usersData.map((row, idx) => normalizeUserRow(row, idx));
    }
  }

  return {
    request_id: `bulk-onboard-${timestamp}`,
    company: companyRow,
    superuser: superuserRow,
    users: userRows,
    options: {
      allow_partial_user_success: true,
    },
  };
};
